import { Button, Dialog, TextField } from "@metatrustlabs/ui-base";
import { ChangeEvent, FC, useCallback, useState } from "react";
import api, { apiList } from "api";
import { toast } from "react-toastify";
import { AIIntegrationOrigin, integraiton } from "common/types";
import { debounce } from "utils/debounce";
import { useRouter } from "next/router";
import { ROUTE } from "common/const";

type AIIntegrationSettingProps = {
  client: string;
  name: string;
  data: integraiton | undefined;
  onClose: () => void;
  visible: boolean;
  origin: AIIntegrationOrigin;
  deleteCallback: () => void;
};
const AIIntegrationSetting: FC<AIIntegrationSettingProps> = ({
  client,
  name,
  data,
  onClose,
  visible,
  origin,
  deleteCallback,
}) => {
  const router = useRouter();
  const [apiKey, setApiKey] = useState<string>(data?.attributes?.apiKey || "");
  const [inputErrer, setInputErrer] = useState("");
  const [isEdit, setIsEdit] = useState(
    Boolean(data?.attributes?.apiKey) === false || false
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isOpenConfigure, setIsOpenConfigure] = useState(false);

  const handleDeleteIntegration = useCallback(() => {
    api(apiList.integration.delete, { type: client })
      .then((res) => {
        toast.success("Removed successfully!");
        deleteCallback();
        onClose();
      })
      .catch((err) => {
        toast.error(err.message);
      });
  }, [client, onClose, deleteCallback]);

  const checkApiKey = () => {
    const regex = /^sk-[a-zA-Z0-9]{48}$/;
    if (!apiKey || !regex.test(apiKey)) {
      return false;
    }
    return true;
  };

  const handleCreateOrUpdateAIIntegration = () => {
    if (!checkApiKey()) {
      setInputErrer("There is no Open AI API input");
      return;
    }
    setIsLoading(true);
    if (data?.attributes?.apiKey) {
      api(apiList.integration.update, { client, apiKey })
        .then(() => {
          setIsLoading(false);
          toast.success("Saved successfully!");
          deleteCallback();
          onClose();
        })
        .catch((err) => {
          toast.error("Failed to save, please try again later!");
          setIsLoading(false);
        });
    } else {
      api(apiList.integration.create, { client, apiKey })
        .then(() => {
          setIsLoading(false);
          toast.success("Saved successfully!");
          deleteCallback();
          onClose();
        })
        .catch((err) => {
          toast.error("Failed to save, please try again later!");
          setIsLoading(false);
        });
    }
  };

  return (
    <Dialog
      onClose={() => {
        onClose();
      }}
      cancelable={false}
      isOpen={visible}
      size="sm"
      title={`${name}`}
      className="max-w-2xl"
      closeButton={
        origin === AIIntegrationOrigin.INTEGRATION || isOpenConfigure ? (
          <Button
            size="sm"
            onClick={() => {
              if (isOpenConfigure) {
                setIsOpenConfigure(false);
                return;
              }
              if (!isEdit) {
                handleDeleteIntegration();
              } else {
                onClose();
              }
            }}
            intent={"primary"}
          >
            {isEdit ? "Cancel" : "Remove"}
          </Button>
        ) : (
          <></>
        )
      }
      confirmButton={
        origin === AIIntegrationOrigin.INTEGRATION || isOpenConfigure ? (
          <Button
            size="sm"
            isLoading={isLoading}
            intent="highlight"
            onClick={() => {
              if (!isEdit) {
                setIsEdit(true);
                return;
              }
              handleCreateOrUpdateAIIntegration();
            }}
          >
            {isEdit ? "Save" : "Edit"}
          </Button>
        ) : (
          <></>
        )
      }
    >
      <div className="p-12">
        {origin === AIIntegrationOrigin.STARTSCAN && (
          <div className="space-y-8">
            <p className="text-gray-800 text-2xl font-medium text-center">
              Go Premium for Advanced AI
            </p>
            <p className="text-gray-600 text-sm leading-6">
              The current organization is on a free subscription. To use any of
              our advanced AI-based features, you can upgrade to a{" "}
              <span className="font-bold">Premium</span> plan, or manually
              configure an <span className="font-bold">OpenAI integration</span>
              .
            </p>
            <div className="flex items-center mx-auto justify-center gap-2">
              <Button
                intent="secondary"
                onClick={() => setIsOpenConfigure(true)}
              >
                Continue with Free Plan
              </Button>
              <Button
                intent="highlight"
                onClick={() => {
                  router.push(ROUTE.subscription.select);
                }}
              >
                Upgrade to Premium
              </Button>
            </div>
          </div>
        )}
        {origin === AIIntegrationOrigin.STARTSCAN && isOpenConfigure && (
          <div className="flex items-center justify-between pt-8">
            <div className="flex-1 h-0 border boeder-gray-200 mr-5"></div>
            <p className="text-gray-400 text-sm">
              Configure Open AI Integration
            </p>
            <div className="flex-1 h-0 border boeder-gray-200 ml-5"></div>
          </div>
        )}
        {(origin === AIIntegrationOrigin.INTEGRATION || isOpenConfigure) && (
          <>
            <div className="flex items-center justify-between pt-4">
              <p className="text-gray-600 text-sm font-medium">
                OpenAI API Key
              </p>
              <a
                href="https://platform.openai.com/docs/guides/production-best-practices/api-keys"
                className="text-orange-500 hover:text-orange-600 text-sm"
                target="_blank"
                rel="noreferrer"
              >
                How to obain an API Key?
              </a>
            </div>

            <TextField
              defaultValue={apiKey}
              disabled={!isEdit || isLoading}
              placeholder="sk-...."
              onChange={debounce((e: ChangeEvent<HTMLInputElement>) => {
                setApiKey(e.target.value);
              }, 300)}
              className="pt-4"
            ></TextField>
            {inputErrer && (
              <p className="text-xs text-blue-500 pt-2">{inputErrer}</p>
            )}
          </>
        )}
      </div>
    </Dialog>
  );
};

export default AIIntegrationSetting;
